import i18next from 'i18next';
import React from 'react';

import { DefaultBooleanIcon, DefaultDateIcon, DefaultIntegerIcon, DefaultStringIcon, MailIcon } from '~/assets';
import {
  AdultsInHouseholdFilterIcon,
  AgeFilterIcon,
  BankCardOwnerFilterIcon,
  BehaviorsFilterIcon,
  ChildrenAgeRangesFilterIcon,
  ChildrenInHouseholdFilterIcon,
  CityFilterIcon,
  CompanyCityFilterIcon,
  CompanyDomainFilterIcon,
  CompanyEmployeeCountFilterIcon,
  CompanyNameFilterIcon,
  CompanyRevenueFilterIcon,
  CompanySICFilterIcon,
  CompanyStateFilterIcon,
  CreditCardOwnerFilterIcon,
  CreditRangeFilterIcon,
  DefaultFilterIcon,
  DepartmentFilterIcon,
  EducationFilterIcon,
  EthnicityFilterIcon,
  FinancialFilterIcon,
  GenderFilterIcon,
  GenerationsInHouseholdFilterIcon,
  HomeDwellingTypeFilterIcon,
  HomeHeatTypeFilterIcon,
  HomeOwnershipFilterIcon,
  HomePriceFilterIcon,
  HomePurchasedYearsAgoFilterIcon,
  HomeValueFilterIcon,
  HouseHoldFilterIcon,
  IncomePremiumHouseholdFilterIcon,
  InterestFilterIcon,
  InvestmentsOwnerFilterIcon,
  JobTitleFilterIcon,
  LanguageFilterIcon,
  MaritalStatusFilterIcon,
  MortgageAgeFilterIcon,
  MortgageLoanAmountFilterIcon,
  MortgageLoanTypeFilterIcon,
  MortgageRefinanceTypeFilterIcon,
  MortgageRefinementAgeFilterIcon,
  MortgageRefinementAmountFilterIcon,
  MultilingualFilterIcon,
  NetWorthFilterIcon,
  NetWorthHouseholdFilterIcon,
  NewCreditOfferedFilterIcon,
  NumberOfVehiclesInHouseholdFilterIcon,
  OccupationsFilterIcon,
  PeopleInHouseholdFilterIcon,
  PersonalityFilterIcon,
  PoliticalContributorFilterIcon,
  PoliticalFilterIcon,
  PremiumAmexFilterIcon,
  PremiumCardOwnerFilterIcon,
  PrimaryIndustryFilterIcon,
  RegularAmexFilterIcon,
  ReligionFilterIcon,
  SeniorityLevelFilterIcon,
  StateFilterIcon,
  StocksAndBondsOwnerFilterIcon,
  UrbanicityFilterIcon,
  VeteranInHouseholdFilterIcon,
} from '~/assets/globalSearchFilters';
import { Filter } from '~/contexts/FilterContext';
import {
  BooleanOperators,
  DateOperators,
  ExtendedStringOperators,
  NumberOperators,
  StringOperators,
} from '~/types/api.graphql';
import {
  FieldType,
  FilterExtendedStringField,
  FilterField,
  FilterStringField,
  LogicalOperator,
  PersonFilter,
  PersonFilterInput,
} from '~/types/api.graphql';
import { FieldTypeExtended } from '~/utils/filters';

// String Operators
export type StringOperatorKeys = keyof StringOperators;
export enum StringOperatorOperatorKey {
  eq = 'eq',
  in = 'in',
  exists = 'exists',
}
export const STRING_OPERATOR_KEYS: StringOperatorKeys[] = [
  StringOperatorOperatorKey.eq,
  StringOperatorOperatorKey.in,
  StringOperatorOperatorKey.exists,
];
// Boolean Operators
export type BooleanOperatorKeys = keyof BooleanOperators;
export const BOOLEAN_OPERATOR_KEYS: BooleanOperatorKeys[] = ['eq', 'exists'];

// Date Operators
export type DateOperatorKeys = keyof DateOperators;
export const DATE_OPERATOR_KEYS: DateOperatorKeys[] = ['between', 'gt', 'gte', 'lt', 'lte', 'exists'];
export const BETWEEN_OPERATOR: DateOperatorKeys = 'between';
export const EXISTS_OPERATOR: DateOperatorKeys = 'exists';
export const OTHER_DATE_OPERATORS: DateOperatorKeys[] = DATE_OPERATOR_KEYS.filter(
  (operator) => operator !== BETWEEN_OPERATOR && operator !== EXISTS_OPERATOR,
);

// Extended String Operators
export type ExtendedStringOperatorKeys = keyof ExtendedStringOperators;
export enum ExtendedStringOperatorKey {
  eq = 'eq',
  startsWith = 'startsWith',
  contains = 'contains',
}
export const EXTENDED_STRING_OPERATOR_KEYS: ExtendedStringOperatorKeys[] = [
  ExtendedStringOperatorKey.eq,
  ExtendedStringOperatorKey.startsWith,
  ExtendedStringOperatorKey.contains,
];

// Number Operators
export type NumberOperatorKeys = keyof NumberOperators;
export enum NumberOperatorOperatorKey {
  eq = 'eq',
  gt = 'gt',
  gte = 'gte',
  lt = 'lt',
  lte = 'lte',
  between = 'between',
  exists = 'exists',
}
export const NUMBER_OPERATOR_KEYS: NumberOperatorKeys[] = [
  NumberOperatorOperatorKey.eq,
  NumberOperatorOperatorKey.gt,
  NumberOperatorOperatorKey.gte,
  NumberOperatorOperatorKey.lt,
  NumberOperatorOperatorKey.lte,
  NumberOperatorOperatorKey.between,
  NumberOperatorOperatorKey.exists,
];

type StringFilterFormValue = string | string[];
type CustomFilterFormValue = string;
type NumberFilterFormValue = string | string[];
type BooleanFilterFormValue = boolean;
type DateFilterFormValue = string;

export interface FilterAnswer {
  title: string;
  disabled?: boolean;
}

export interface FilterConditionItem {
  key: string;
  text: string;
  value: string;
  negate?: boolean;
}

export interface FilterAnswers {
  items: FilterAnswer[];
  separator?: string;
  disableEdit?: boolean;
}

abstract class FilterHelper<FormValue = any> {
  protected filter: Filter;

  constructor(filter: Filter) {
    this.filter = filter;
  }

  public static createInstance(filter: Filter): FilterHelper {
    if (!filter) return null;
    switch (filter.type) {
      case FieldType.String:
        return new StringFilterHelper(filter);
      case FieldType.Number:
        return new NumberFilterHelper(filter);
      case FieldType.Boolean:
        return new BooleanFilterHelper(filter);
      case FieldType.Date:
        return new DateFilterHelper(filter);
      case FieldTypeExtended.Custom: {
        if (filter.filterField === FilterExtendedStringField.PixelHitUrl) {
          return new PixelHitUrlFilterHelper(filter);
        }
        return new CustomFilterHelper(filter);
      }
    }
  }

  abstract getFilterDescription(): string;

  private FilterIconMap = {
    // noew fields
    [FilterField.AdultsInHousehold]: <AdultsInHouseholdFilterIcon />,
    [FilterField.Age]: <AgeFilterIcon />,
    [FilterField.Email]: <MailIcon />,
    [FilterField.AmexCard]: <RegularAmexFilterIcon />,
    [FilterField.BankCard]: <BankCardOwnerFilterIcon />,
    [FilterField.Behaviors]: <BehaviorsFilterIcon />,
    [FilterField.ChildrenAgeRanges]: <ChildrenAgeRangesFilterIcon />,
    [FilterField.ChildrenInHousehold]: <ChildrenInHouseholdFilterIcon />,
    [FilterField.City]: <CityFilterIcon />,
    [FilterField.CreditCard]: <CreditCardOwnerFilterIcon />,
    [FilterField.CreditRange]: <CreditRangeFilterIcon />,
    [FilterField.DwellingType]: <HomeDwellingTypeFilterIcon />,
    [FilterField.Education]: <EducationFilterIcon />,
    [FilterField.EthnicGroup]: <EthnicityFilterIcon />,
    [FilterField.Gender]: <GenderFilterIcon />,
    [FilterField.Generation]: <GenerationsInHouseholdFilterIcon />,
    [FilterField.HomeHeatType]: <HomeHeatTypeFilterIcon />,
    [FilterField.HomeOwner]: <HomeOwnershipFilterIcon />,
    [FilterField.HomePrice]: <HomePriceFilterIcon />,
    [FilterField.HomePurchasedYearsAgo]: <HomePurchasedYearsAgoFilterIcon />,
    [FilterField.HomeValue]: <HomeValueFilterIcon />,
    [FilterField.HouseholdIncome]: <HouseHoldFilterIcon />,
    [FilterField.HouseholdNetWorth]: <NetWorthHouseholdFilterIcon />,
    [FilterField.IncomeLevels]: <FinancialFilterIcon />,
    [FilterField.Interests]: <InterestFilterIcon />,
    [FilterField.Language]: <LanguageFilterIcon />,
    [FilterField.MaritalStatus]: <MaritalStatusFilterIcon />,
    [FilterField.MortgageAge]: <MortgageAgeFilterIcon />,
    [FilterField.MortgageAmount]: <MortgageLoanAmountFilterIcon />,
    [FilterField.MortgageLoanType]: <MortgageLoanTypeFilterIcon />,
    [FilterField.MortgageRefinanceAge]: <MortgageRefinementAgeFilterIcon />,
    [FilterField.MortgageRefinanceAmount]: <MortgageRefinementAmountFilterIcon />,
    [FilterField.MortgageRefinanceType]: <MortgageRefinanceTypeFilterIcon />,
    [FilterField.Multilingual]: <MultilingualFilterIcon />,
    [FilterField.NetWorth]: <NetWorthFilterIcon />,
    [FilterField.NewCreditOfferedHousehold]: <NewCreditOfferedFilterIcon />,
    [FilterField.NumberOfVehiclesInHousehold]: <NumberOfVehiclesInHouseholdFilterIcon />,
    [FilterField.OccupationDetail]: <OccupationsFilterIcon />,
    [FilterField.OwnsInvestment]: <InvestmentsOwnerFilterIcon />,
    [FilterField.OwnsStocksAndBonds]: <StocksAndBondsOwnerFilterIcon />,
    [FilterField.PeopleInHousehold]: <PeopleInHouseholdFilterIcon />,
    [FilterField.Personality]: <PersonalityFilterIcon />,
    [FilterField.PoliticalContributor]: <PoliticalContributorFilterIcon />,
    [FilterField.PoliticalParty]: <PoliticalFilterIcon />,
    [FilterField.PremiumAmexCardOwner]: <PremiumAmexFilterIcon />,
    [FilterField.PremiumCard]: <PremiumCardOwnerFilterIcon />,
    [FilterField.PremiumIncomeHousehold]: <IncomePremiumHouseholdFilterIcon />,
    [FilterField.Religion]: <ReligionFilterIcon />,
    [FilterField.State]: <StateFilterIcon />,
    [FilterField.Urbanicity]: <UrbanicityFilterIcon />,
    [FilterField.VeteransInHousehold]: <VeteranInHouseholdFilterIcon />,
    [FilterField.JobTitle]: <JobTitleFilterIcon />,
    [FilterField.SeniorityLevel]: <SeniorityLevelFilterIcon />,
    [FilterField.CompanyName]: <CompanyNameFilterIcon />,
    [FilterField.PrimaryIndustry]: <PrimaryIndustryFilterIcon />,
    [FilterField.Department]: <DepartmentFilterIcon />,
    [FilterField.CompanyRevenue]: <CompanyRevenueFilterIcon />,
    [FilterField.CompanyCity]: <CompanyCityFilterIcon />,
    [FilterField.CompanyDomain]: <CompanyDomainFilterIcon />,
    [FilterField.CompanyEmployeeCount]: <CompanyEmployeeCountFilterIcon />,
    [FilterField.CompanySic]: <CompanySICFilterIcon />,
    [FilterField.CompanyState]: <CompanyStateFilterIcon />,
    [FilterField.PostalCode]: <StateFilterIcon />,
    [FilterField.Dma]: <StateFilterIcon />,
    [FilterField.BusinessEmail]: <MailIcon />,
  };

  private FilterTextMap = {
    [FilterField.PostalCode]: i18next.t('audience.people.zip'),
  };

  getIcon = () => {
    if (this.FilterIconMap[this.filter.filterField]) return this.FilterIconMap[this.filter.filterField];
    if (this.filter.type === FieldType.Number) return <DefaultIntegerIcon />;
    if (this.filter.type === FieldType.String) return <DefaultStringIcon />;
    if (this.filter.type === FieldType.Boolean) return <DefaultBooleanIcon />;
    if (this.filter.type === FieldType.Date) return <DefaultDateIcon />;
    return <DefaultFilterIcon />;
  };

  getText = () => {
    return this.FilterTextMap[this.filter.filterField] || this.filter.title;
  };

  getField(): string {
    return this.filter.isCustom ? this.filter.title : this.filter.filterField;
  }

  abstract getAnswers(selectedFilter: PersonFilter): FilterAnswers;
  abstract removeAnswer(selectedFilter: PersonFilter, answer: FilterAnswer): PersonFilter;
  abstract mergedAnswers(audienceFilter: PersonFilter, segmentFilter: PersonFilter): FilterAnswers;
  abstract getConditionItems(): FilterConditionItem[];
  abstract formFilter(filterField, condition, filterValue: FormValue, negate?: boolean): PersonFilter;
}

class StringFilterHelper extends FilterHelper<StringFilterFormValue> {
  getAnswers(selectedFilter: PersonFilter): FilterAnswers {
    const items = selectedFilter?.stringFilter?.operator?.in?.map((option) => ({ title: option }));
    const separator = this.filter.configuration?.stringFilterConfiguration.condition;
    return { items: items || [], separator };
  }

  removeAnswer(selectedFilter: PersonFilter, answer: FilterAnswer): PersonFilter {
    // deep copy
    const filterTemp = JSON.parse(JSON.stringify(selectedFilter));
    const index = filterTemp.stringFilter?.operator?.in?.indexOf(answer.title);
    if (index !== -1) {
      filterTemp.stringFilter?.operator?.in?.splice(index, 1);
    }
    return filterTemp.stringFilter?.operator?.in?.length > 0 && filterTemp;
  }

  mergedAnswers(audienceFilter: PersonFilter, segmentFilter: PersonFilter): FilterAnswers {
    const separator = this.filter.configuration?.stringFilterConfiguration.condition;
    let disableEdit = false;
    let items: FilterAnswer[] = [];
    if (separator === LogicalOperator.And) {
      const itemsAudience =
        audienceFilter?.stringFilter?.operator?.in?.map((option) => ({ title: option, disabled: true })) || [];
      const itemsSegment =
        segmentFilter?.stringFilter?.operator?.in?.map((option) => ({ title: option, disabled: false })) || [];
      items = itemsAudience.concat(itemsSegment);
    } else {
      items =
        (segmentFilter || audienceFilter)?.stringFilter?.operator?.in?.map((option) => ({
          title: option,
          disabled: true,
        })) || [];
      // Disable editing for OR condition if there is only 1 option selected
      disableEdit = audienceFilter?.stringFilter?.operator?.in?.length === 1;
    }

    return { items, separator, disableEdit };
  }

  getFilterDescription(): string {
    return FilterStringField[this.filter.filterField]
      ? `${i18next.t('newGlobalSearchAudience.select')} ${this.filter.title.toLocaleLowerCase()}`
      : `${i18next.t('newGlobalSearchAudience.enter')} ${this.filter.title.toLocaleLowerCase()}`;
  }

  getConditionItems(): FilterConditionItem[] {
    const onlyExistenceOperator = this.filter?.configuration?.stringFilterConfiguration?.onlyExistence;
    const filteredStringKeys = onlyExistenceOperator ? [StringOperatorOperatorKey.exists] : STRING_OPERATOR_KEYS;

    return filteredStringKeys
      .map((key) => ({
        key,
        text: i18next.t(`filterConditions.${key}`),
        value: key,
        negate: false,
      }))
      .concat(
        filteredStringKeys.map((key) => ({
          key,
          text: i18next.t(`filterConditions.${key}Negate`),
          value: key,
          negate: true,
        })),
      );
  }

  formFilter(filterField, condition, filterValue: StringFilterFormValue, negate?: boolean): PersonFilter {
    const fieldConfig = this.filter.isCustom ? { customField: this.filter.title } : { field: filterField };

    return {
      stringFilter: {
        ...fieldConfig,
        negate: condition === StringOperatorOperatorKey.exists ? undefined : negate,
        operator: {
          [condition]: condition === StringOperatorOperatorKey.eq ? filterValue?.[0] : filterValue,
        },
      },
    };
  }
}

export interface NumberFilterBounds {
  min: number;
  max: number;
}

export class NumberFilterHelper extends FilterHelper<NumberFilterFormValue> {
  getAnswers(selectedFilter: PersonFilter): FilterAnswers {
    const from = selectedFilter?.numberFilter?.operator.between.from;
    const to = selectedFilter?.numberFilter?.operator.between.to;
    const items = selectedFilter?.numberFilter?.operator.between ? [{ title: `${from} - ${to}` }] : [];
    return { items: items || [] };
  }

  removeAnswer(): PersonFilter {
    return null;
  }

  extractSegmentFilter(audienceFilter: PersonFilter, selectedFilter: PersonFilter): PersonFilter {
    return selectedFilter || audienceFilter;
  }

  mergedAnswers(audienceFilter: PersonFilter, segmentFilter: PersonFilter): FilterAnswers {
    const answers = this.getAnswers(segmentFilter || audienceFilter);
    answers.items.forEach((item) => (item.disabled = true));
    return { ...answers };
  }

  getFilterDescription(): string {
    return `${i18next.t('newGlobalSearchAudience.enter')} ${this.filter.title.toLocaleLowerCase()}`;
  }

  getBounds = (): NumberFilterBounds => {
    return {
      min: this.filter.configuration?.numberFilterConfiguration?.from,
      max: this.filter.configuration?.numberFilterConfiguration?.to,
    };
  };

  getConditionItems(): FilterConditionItem[] {
    return NUMBER_OPERATOR_KEYS.map((key) => ({
      key,
      text: i18next.t(`filterConditions.${key}`),
      value: key,
      negate: false,
    })).concat([
      {
        key: 'exists',
        text: i18next.t(`filterConditions.existsNegate`),
        value: 'exists',
        negate: true,
      },
    ]);
  }

  formFilter(filterField, condition, filterValue: NumberFilterFormValue): PersonFilter {
    const fieldConfig = this.filter.isCustom ? { customField: this.filter.title } : { field: filterField };

    return {
      numberFilter: {
        ...fieldConfig,
        operator: {
          [condition]: ['between', 'exists'].includes(condition as string) ? filterValue : Number(filterValue),
        },
      },
    };
  }
}

class BooleanFilterHelper extends FilterHelper<BooleanFilterFormValue> {
  getAnswers(selectedFilter: PersonFilter): FilterAnswers {
    const items = selectedFilter?.booleanFilter
      ? [{ title: selectedFilter?.booleanFilter?.operator?.eq ? i18next.t('common.yes') : i18next.t('common.no') }]
      : [];
    return { items: items || [] };
  }

  removeAnswer(): PersonFilter {
    return null;
  }

  getFilterDescription(): string {
    return `${i18next.t('newGlobalSearchAudience.select')}  ${this.filter.title.toLocaleLowerCase()}`;
  }

  mergedAnswers(audienceFilter: PersonFilter, segmentFilter: PersonFilter): FilterAnswers {
    const answers = this.getAnswers(audienceFilter || segmentFilter);
    answers.items.forEach((item) => (item.disabled = !!audienceFilter?.booleanFilter));
    return { ...answers, disableEdit: !!audienceFilter?.booleanFilter };
  }

  getConditionItems(): FilterConditionItem[] {
    return BOOLEAN_OPERATOR_KEYS.map((key) => ({
      key,
      text: i18next.t(`filterConditions.${key}`),
      value: key,
      negate: false,
    })).concat([
      {
        key: 'exists',
        text: i18next.t(`filterConditions.existsNegate`),
        value: 'exists',
        negate: true,
      },
    ]);
  }

  formFilter(filterField, condition, filterValue: BooleanFilterFormValue): PersonFilter {
    const fieldConfig = this.filter.isCustom ? { customField: this.filter.title } : { field: filterField };

    return {
      booleanFilter: {
        ...fieldConfig,
        operator: {
          [condition]: filterValue,
        },
      },
    };
  }
}

export class DateFilterHelper extends FilterHelper<DateFilterFormValue> {
  getAnswers(selectedFilter: PersonFilter): FilterAnswers {
    const items = selectedFilter?.dateFilter ? [] : [];
    return { items: items || [] };
  }

  removeAnswer(): PersonFilter {
    throw new Error('Method not implemented.');
  }

  getFilterDescription(): string {
    return `${i18next.t('newGlobalSearchAudience.enter')}  ${this.filter.title.toLocaleLowerCase()}`;
  }

  mergedAnswers(audienceFilter: PersonFilter, segmentFilter: PersonFilter): FilterAnswers {
    return this.getAnswers(audienceFilter || segmentFilter);
  }

  getConditionItems(): FilterConditionItem[] {
    return DATE_OPERATOR_KEYS.map((key) => ({
      key,
      text: i18next.t(`filterConditions.${key}`),
      value: key,
      negate: false,
    })).concat([
      {
        key: 'exists',
        text: i18next.t(`filterConditions.existsNegate`),
        value: 'exists',
        negate: true,
      },
    ]);
  }

  formFilter(filterField, condition, filterValue: DateFilterFormValue): PersonFilter {
    const fieldConfig = this.filter.isCustom ? { customField: this.filter.title } : { field: filterField };

    return {
      dateFilter: {
        ...fieldConfig,
        operator: {
          [condition]: filterValue,
        },
      },
    };
  }
}

class CustomFilterHelper extends FilterHelper<CustomFilterFormValue> {
  getFilterDescription(): string {
    return `${i18next.t('newGlobalSearchAudience.select')} ${this.filter.title.toLocaleLowerCase()}`;
  }
  getAnswers(): FilterAnswers {
    return null;
  }
  removeAnswer(): PersonFilter {
    return null;
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  mergedAnswers(audienceFilter: PersonFilter, segmentFilter: PersonFilter): FilterAnswers {
    return null;
  }

  getConditionItems(): FilterConditionItem[] {
    return BOOLEAN_OPERATOR_KEYS.map((key) => ({
      key,
      text: i18next.t(`filterConditions.${key}`),
      value: key,
    }));
  }

  formFilter(filterField, condition, filterValue: CustomFilterFormValue): PersonFilter {
    const fieldConfig = this.filter.isCustom ? { customField: this.filter.title } : { field: filterField };

    return {
      stringFilter: {
        ...fieldConfig,
        operator: {
          [condition]: filterValue,
        },
      },
    };
  }
}

export const extendedStringFilterOperators = [
  ExtendedStringOperatorKey.startsWith,
  ExtendedStringOperatorKey.contains,
  ExtendedStringOperatorKey.eq,
];

export class PixelHitUrlFilterHelper extends CustomFilterHelper {
  static getPathFilterInputOperator = (filter: PersonFilterInput) => {
    if (!filter) return undefined;
    // Since the filter's operator is coming in the form
    // {operator: value} i.e {startsWith: value} or {contains: null}
    // We need to find which extendedStringFilterOperators matches the object key.
    // The value can be null because the filter is being filled in different steps.
    // It will be null until the user selects the actual value that occurs after selecting the operator.
    return extendedStringFilterOperators.find(
      (operator) => Object.keys(filter.extendedStringFilter?.operator || {})?.[0] === operator,
    );
  };

  private getPathFilterOperator = (filter: PersonFilter) => {
    if (!filter) return undefined;
    return extendedStringFilterOperators.find((operator) => !!filter.extendedStringFilter?.operator?.[operator]);
  };

  mergedAnswers(audienceFilter: PersonFilter, segmentFilter: PersonFilter): FilterAnswers {
    const filterOperator = this.getPathFilterOperator(segmentFilter) || this.getPathFilterOperator(audienceFilter);
    if (!filterOperator) return null;
    const value = segmentFilter?.extendedStringFilter?.operator?.[filterOperator];
    return { items: [{ title: `${i18next.t(`components.pathSelector.operators.${filterOperator}`)}: ${value}` }] };
  }

  getConditionItems(): FilterConditionItem[] {
    return EXTENDED_STRING_OPERATOR_KEYS.map((key) => ({
      key,
      text: i18next.t(`filterConditions.${key}`),
      value: key,
      negate: false,
    })).concat(
      EXTENDED_STRING_OPERATOR_KEYS.map((key) => ({
        key,
        text: i18next.t(`filterConditions.${key}Negate`),
        value: key,
        negate: true,
      })),
    );
  }

  formFilter(filterField, condition, filterValue: CustomFilterFormValue, negate?: boolean): PersonFilter {
    return {
      extendedStringFilter: {
        field: filterField,
        operator: {
          [condition]: filterValue,
        },
        negate,
      },
    };
  }
}

export default FilterHelper;
